// CompareView.jsx
//
// Multi-model comparison of the baseline, PROJECTED across the selected
// calendar window (with calendar effect applied via the endpoint's
// cal_factor; persona effects are a no-op until the forecast-side assignment
// shares exist).
//
// Views:
//   1) Window summary table (per model, all collection paths)
//   2) Per-bucket collections $ — GROUPED bars: one group per time bucket,
//      one bar per model side by side, each model's bar stacked by channel.
//      Mode pill toggles Per period vs Cumulative.
//   3) Channel impact — stacked bars, one bar per model, segmented by channel
//   4) By-DPD matrix
//
// Pills:
//   View         All (default) / PTP / Payments  → which collection path
//   Granularity  Day (default) / Weekly / Monthly → x-axis bucketing
//   Mode         Per period (default) / Cumulative
//   Date range   drives the projection window

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, Typography,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart, Bar, ComposedChart, Line, ReferenceLine,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList,
} from "recharts";

import {
  getForecastModels,
  getBaselineCompare,
} from "../../services/forecast/forecastservices";
import {
  BLUE, GREEN, AMBER, PURPLE, PINK,
  RANGE_OPTIONS, getForecastDateWindow,
  PillGroup, MultiSelectDropdown,
  SectionTitle, EmptyState,
} from "./forecastShared";


const FILTER_OPTIONS = ["All", "PTP", "Payments"];
const GRAN_OPTIONS   = ["Day", "Weekly", "Monthly"];

const MODEL_COLORS   = [BLUE, GREEN, PURPLE, AMBER, PINK, "#E0529C", "#3DA5A0", "#9B6BDF"];
const CHANNEL_COLORS = [BLUE, GREEN, PURPLE, AMBER, PINK, "#E0529C", "#3DA5A0", "#C9A227", "#7E8AA2"];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];


// ── Formatters ──────────────────────────────────────────────────────
function fmtCurrency(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "$0";
  if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1_000)     return `$${(num / 1_000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}
function fmtCount(n) {
  const num = Number(n);
  if (!isFinite(num)) return "0";
  return Math.round(num).toLocaleString();
}
function fmtPct(num, decimals = 2) {
  const n = Number(num);
  if (!isFinite(n)) return "—";
  return `${(n * 100).toFixed(decimals)}%`;
}
function ymd(d) {
  if (!d) return undefined;
  if (typeof d === "string") return d.slice(0, 10);
  const dt = d instanceof Date ? d : new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function shortDate(s) {
  const str = String(s).slice(0, 10);
  const [, m, d] = str.split("-");
  return m && d ? `${Number(m)}/${Number(d)}` : str;
}

// Monday (ISO week start) for a YYYY-MM-DD string, as a YYYY-MM-DD string.
function weekStart(dateStr) {
  const d = new Date(dateStr + "T00:00:00Z");
  const dow = (d.getUTCDay() + 6) % 7;           // 0 = Mon … 6 = Sun
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}

// Bucket a date into { key (sortable), label (display) } by granularity.
function bucketOf(dateStr, gran) {
  if (gran === "Monthly") {
    const ym = dateStr.slice(0, 7);
    const [y, m] = ym.split("-");
    return { key: ym, label: `${MONTHS[Number(m) - 1]} ${y}` };
  }
  if (gran === "Weekly") {
    const ws = weekStart(dateStr);
    return { key: ws, label: `wk ${shortDate(ws)}` };
  }
  return { key: dateStr, label: shortDate(dateStr) };
}


// ── Self-contained box-and-whisker chart (no recharts dependency) ───
// One box per model: whisker low→high, box = middle 50% (Q1–Q3), median
// line, low/high labeled at the caps. Native <title> gives a hover summary.
function ModelBoxPlot({ stats, height = 400, periodWord = "day" }) {
  const ref = React.useRef(null);
  const [w, setW] = useState(640);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.clientWidth);
    const ro = new ResizeObserver((entries) => setW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const padL = 72, padR = 24, padT = 28, padB = 42;
  const innerW = Math.max(10, w - padL - padR);
  const innerH = Math.max(10, height - padT - padB);

  const hasStats = stats && stats.length > 0;
  const dataLo = hasStats ? Math.min(...stats.map((s) => s.min)) : 0;
  const dataHi = hasStats ? Math.max(...stats.map((s) => s.max)) : 1;
  const span   = (dataHi - dataLo) || dataHi || 1;
  const yLo    = Math.max(0, dataLo - span * 0.18);
  const yHi    = dataHi + span * 0.18;
  const yToPx  = (v) => padT + innerH * (1 - (v - yLo) / ((yHi - yLo) || 1));

  const n     = hasStats ? stats.length : 1;
  const slot  = innerW / n;
  const boxW  = Math.min(72, slot * 0.45);

  const TICKS = 5;
  const tickVals = Array.from({ length: TICKS + 1 }, (_, i) => yLo + (yHi - yLo) * (i / TICKS));
  const shortName = (s) => (s && s.length > 26 ? s.slice(0, 24) + "…" : s);

  return (
    <Box ref={ref} sx={{ width: "100%", height }}>
      {hasStats && (
        <svg width={w} height={height} style={{ display: "block" }}>
          {/* grid + y labels */}
          {tickVals.map((tv, i) => {
            const y = yToPx(tv);
            return (
              <g key={`t${i}`}>
                <line x1={padL} x2={w - padR} y1={y} y2={y} stroke="rgba(0,0,0,0.07)" strokeDasharray="3 3" />
                <text x={padL - 8} y={y + 3} textAnchor="end" fontSize={11} fill="#888">{fmtCurrency(tv)}</text>
              </g>
            );
          })}

          {stats.map((s, idx) => {
            const cx = padL + slot * idx + slot / 2;
            const x0 = cx - boxW / 2, x1 = cx + boxW / 2;
            const yHiPx = yToPx(s.max), yLoPx = yToPx(s.min);
            const yQ1 = yToPx(s.q1), yQ3 = yToPx(s.q3), yMed = yToPx(s.median);
            const boxTop = Math.min(yQ1, yQ3), boxH = Math.max(1, Math.abs(yQ1 - yQ3));
            return (
              <g key={s.id}>
                <title>{`${s.name}
High      ${fmtCurrency(s.max)}
Upper 25% ${fmtCurrency(s.q3)}
Median    ${fmtCurrency(s.median)}
Lower 25% ${fmtCurrency(s.q1)}
Low       ${fmtCurrency(s.min)}
across ${s.n} ${periodWord}${s.n === 1 ? "" : "s"}`}</title>

                {/* whisker low → high + caps */}
                <line x1={cx} x2={cx} y1={yHiPx} y2={yLoPx} stroke={s.color} strokeWidth={1.5} />
                <line x1={cx - boxW * 0.28} x2={cx + boxW * 0.28} y1={yHiPx} y2={yHiPx} stroke={s.color} strokeWidth={1.5} />
                <line x1={cx - boxW * 0.28} x2={cx + boxW * 0.28} y1={yLoPx} y2={yLoPx} stroke={s.color} strokeWidth={1.5} />
                {/* box (middle 50%) */}
                <rect x={x0} y={boxTop} width={boxW} height={boxH}
                      fill={s.color} fillOpacity={0.16} stroke={s.color} strokeWidth={1.5} rx={2} />
                {/* median */}
                <line x1={x0} x2={x1} y1={yMed} y2={yMed} stroke={s.color} strokeWidth={2.25} />
                {/* high / low labels */}
                <text x={cx} y={yHiPx - 7} textAnchor="middle" fontSize={11} fontWeight="700" fill={s.color}>
                  {fmtCurrency(s.max)}
                </text>
                <text x={cx} y={yLoPx + 15} textAnchor="middle" fontSize={11} fontWeight="700" fill={s.color}>
                  {fmtCurrency(s.min)}
                </text>
                {/* model name */}
                <text x={cx} y={height - padB + 20} textAnchor="middle" fontSize={12} fill="#444">
                  {shortName(s.name)}
                </text>
              </g>
            );
          })}
        </svg>
      )}
    </Box>
  );
}

// ── Per-period tooltip (grouped bars + delta line) ──────────────────
function PeriodTooltip({ active, payload, label, modelBars }) {
  if (!active || !payload || payload.length === 0) return null;
  const byKey = new Map(modelBars.map((m) => [`m${m.id}`, m]));
  const bars  = payload.filter((p) => byKey.has(p.dataKey));
  const dP    = payload.find((p) => p.dataKey === "delta");
  return (
    <Box sx={{
      background: "#fff", border: "0.5px solid", borderColor: "divider",
      borderRadius: 1, p: 1, fontSize: 12, minWidth: 190,
      boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
    }}>
      <Box sx={{ fontWeight: 600, mb: 0.5 }}>{label}</Box>
      {bars.map((p) => {
        const m = byKey.get(p.dataKey);
        return (
          <Box key={p.dataKey} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: "1px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
              <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: m.color, flexShrink: 0 }} />
              <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name}</Box>
            </Box>
            <Box sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{fmtCurrency(p.value)}</Box>
          </Box>
        );
      })}
      {dP && (
        <Box sx={{
          mt: 0.5, pt: 0.5, borderTop: "0.5px solid", borderColor: "divider",
          display: "flex", justifyContent: "space-between", gap: 2,
        }}>
          <Box component="span" sx={{ color: "text.secondary" }}>Δ difference</Box>
          <Box component="span" sx={{ fontWeight: 700, color: Number(dP.value) >= 0 ? GREEN : "#A32D2D" }}>
            {Number(dP.value) >= 0 ? "+" : ""}{fmtCurrency(dP.value)}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Percentage labels for the channel stacked bars ──────────────────
// Pick black/white text based on segment fill luminance.
function contrastText(hex) {
  if (typeof hex !== "string" || hex[0] !== "#") return "#fff";
  const h = hex.length === 4
    ? hex.slice(1).split("").map((c) => c + c).join("")
    : hex.slice(1);
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#1a1a1a" : "#fff";
}

// Percentage label centered in a horizontal stacked segment.
// `value` is the segment's precomputed share (0..1) of its model's total,
// so there's no reliance on recharts' row index (which shifts when a model
// has all-zero values). Skips tiny/narrow segments to avoid clutter.
function ChannelPctLabel({ x, y, width, height, value, fill }) {
  const pct = Number(value);
  if (!isFinite(pct) || pct <= 0) return null;
  if (width < 30 || pct < 0.06) return null;   // too narrow / <6% sliver
  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight={700}
      fill={contrastText(fill)}
    >
      {`${Math.round(pct * 100)}%`}
    </text>
  );
}

// Model total $ at the right end of the stacked bar. Anchored to the
// last non-zero segment (carried on `${cn}__endtotal`), so x + width is
// the true end of the whole bar even if trailing channels are zero.
function ChannelTotalLabel({ x, y, width, height, value }) {
  const v = Number(value);
  if (!isFinite(v) || v <= 0) return null;
  return (
    <text
      x={x + width + 6}
      y={y + height / 2}
      textAnchor="start"
      dominantBaseline="central"
      fontSize={11}
      fontWeight={700}
      fill="#444"
    >
      {fmtCurrency(v)}
    </text>
  );
}


export default function CompareView() {
  const [rangePreset,    setRangePreset]    = useState("7D");
  const [filterType,     setFilterType]     = useState("All");
  const [granularity,    setGranularity]    = useState("Day");
  const [selectedModels, setSelectedModels] = useState([]);
  const [models,         setModels]         = useState([]);

  const [compare,        setCompare]        = useState(null);
  const [loadingModels,  setLoadingModels]  = useState(true);
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [error,          setError]          = useState("");

  const metricKey =
    filterType === "PTP"      ? "ptp_amount" :
    filterType === "Payments" ? "payments_amount" :
                                "total_amount";
  const metricLabel =
    filterType === "PTP"      ? "PTP collections $" :
    filterType === "Payments" ? "Payments collections $" :
                                "Total collections $";

  const window = useMemo(() => getForecastDateWindow(rangePreset), [rangePreset]);

  // ── Load models ───────────────────────────────────────────────
  useEffect(() => {
    setLoadingModels(true);
    getForecastModels()
      .then((data) => {
        const list = (data || []).filter((m) => m.is_active);
        setModels(list);
        setSelectedModels(list.map((m) => m.id));
      })
      .catch((e) => {
        console.error("Failed to load models:", e);
        setError("Failed to load models");
      })
      .finally(() => setLoadingModels(false));
  }, []);

  // ── Load compare data on selection / range change ──────────────
  useEffect(() => {
    if (selectedModels.length === 0) { setCompare(null); return; }
    const startDate = ymd(window.forecastStart);
    const endDate   = ymd(window.forecastEnd);
    let cancelled = false;
    setLoadingCompare(true); setError("");
    getBaselineCompare(selectedModels, startDate, endDate)
      .then((data) => { if (!cancelled) setCompare(data); })
      .catch((e) => {
        if (!cancelled) setError(`Failed to load comparison: ${e?.response?.data?.error || e?.message}`);
      })
      .finally(() => { if (!cancelled) setLoadingCompare(false); });
    return () => { cancelled = true; };
  }, [selectedModels, window.forecastStart, window.forecastEnd]);

  const modelOptions = useMemo(
    () => models.map((m) => ({ value: m.id, label: m.name })),
    [models]
  );

  const orderedModels = useMemo(() => {
    const rows = compare?.summary || [];
    return rows.map((r, i) => ({
      model_id:   r.model_id,
      model_name: r.model_name,
      color:      MODEL_COLORS[i % MODEL_COLORS.length],
    }));
  }, [compare]);

  const colorById = useMemo(() => {
    const map = new Map();
    orderedModels.forEach((m) => map.set(m.model_id, m.color));
    return map;
  }, [orderedModels]);

  // ── Per-period per-model TOTALS (no channel split — the channel chart
  //     below covers that). One value per (bucket, model). ──
  const { totalsSeries, modelBars } = useMemo(() => {
    const rows = compare?.by_date || [];

    // bucketKey -> { label, byModel: modelId -> sum }
    const buckets = new Map();
    rows.forEach((r) => {
      const d = String(r.date).slice(0, 10);
      const { key, label } = bucketOf(d, granularity);
      if (!buckets.has(key)) buckets.set(key, { label, byModel: new Map() });
      const bm = buckets.get(key).byModel;
      bm.set(r.model_id, (bm.get(r.model_id) || 0) + (Number(r[metricKey]) || 0));
    });

    const keys = [...buckets.keys()].sort();
    const series = keys.map((k) => {
      const { label, byModel } = buckets.get(k);
      const row = { key: k, label };
      orderedModels.forEach((m) => { row[`m${m.model_id}`] = byModel.get(m.model_id) || 0; });
      return row;
    });

    return {
      totalsSeries: series,
      modelBars: orderedModels.map((m) => ({ id: m.model_id, name: m.model_name, color: m.color })),
    };
  }, [compare, metricKey, granularity, orderedModels]);

  // ── Five-number summary per model (low / Q1 / median / Q3 / high) over
  //     the window's periods, for the box-and-whisker chart. ──
  const boxStats = useMemo(() => {
    const quantile = (sorted, q) => {
      if (sorted.length === 0) return 0;
      if (sorted.length === 1) return sorted[0];
      const pos = (sorted.length - 1) * q;
      const base = Math.floor(pos), rest = pos - base;
      return sorted[base + 1] !== undefined
        ? sorted[base] + rest * (sorted[base + 1] - sorted[base])
        : sorted[base];
    };
    return modelBars.map((m) => {
      const vals = totalsSeries.map((r) => Number(r[`m${m.id}`]) || 0).sort((a, b) => a - b);
      return {
        ...m,
        min:    vals[0] || 0,
        q1:     quantile(vals, 0.25),
        median: quantile(vals, 0.5),
        q3:     quantile(vals, 0.75),
        max:    vals[vals.length - 1] || 0,
        n:      vals.length,
      };
    });
  }, [totalsSeries, modelBars]);

  // ── Per-period series for the right-hand grouped bars + delta line.
  //     Delta = first model − second model (only meaningful for 2 models). ──
  const twoModels = modelBars.length === 2;
  const periodSeries = useMemo(() => {
    if (!twoModels) return totalsSeries;
    const a = modelBars[0].id, b = modelBars[1].id;
    return totalsSeries.map((r) => ({
      ...r,
      delta: (Number(r[`m${a}`]) || 0) - (Number(r[`m${b}`]) || 0),
    }));
  }, [totalsSeries, modelBars, twoModels]);

  const deltaTotal = twoModels
    ? periodSeries.reduce((s, r) => s + (r.delta || 0), 0)
    : 0;

  // ── Channel stacked-bar data (per model, window totals) ─────────
  const { channelBarData, channelNames } = useMemo(() => {
    const byChannel = compare?.by_channel || [];
    const nameOrder = new Map();
    byChannel.forEach((r) => {
      if (!nameOrder.has(r.channel_name)) nameOrder.set(r.channel_name, r.channel_display_order ?? 999);
    });
    const names = [...nameOrder.keys()].sort(
      (a, b) => (nameOrder.get(a) - nameOrder.get(b)) || a.localeCompare(b)
    );
    const lookup = new Map();
    byChannel.forEach((r) => {
      if (!lookup.has(r.model_id)) lookup.set(r.model_id, new Map());
      const cur = lookup.get(r.model_id).get(r.channel_name) || 0;
      lookup.get(r.model_id).set(r.channel_name, cur + (Number(r[metricKey]) || 0));
    });
    const data = orderedModels.map((m) => {
      const row = { model: m.model_name };
      let total = 0;
      names.forEach((cn) => {
        const v = lookup.get(m.model_id)?.get(cn) || 0;
        row[cn] = v;
        total += v;
      });
      // Per-channel share of THIS model's total, read directly by the label.
      names.forEach((cn) => { row[`${cn}__pct`] = total > 0 ? row[cn] / total : 0; });
      // Bar-end total: carry it on the last channel with a positive value,
      // whose segment's right edge is the end of the whole stacked bar.
      let lastPos = null;
      names.forEach((cn) => { if (row[cn] > 0) lastPos = cn; });
      if (lastPos) row[`${lastPos}__endtotal`] = total;
      return row;
    });
    return { channelBarData: data, channelNames: names };
  }, [compare, orderedModels, metricKey]);

  // ── By-DPD matrix ──────────────────────────────────────────────
  const dpdMatrix = useMemo(() => {
    const rows = compare?.by_dpd || [];
    const dims = [];
    const seen = new Set();
    const val = new Map();
    rows.forEach((r) => {
      if (!seen.has(r.dpd_bucket_id)) { seen.add(r.dpd_bucket_id); dims.push({ key: r.dpd_bucket_id, label: r.bucket_label, ord: r.bucket_display_order }); }
      val.set(`${r.model_id}|${r.dpd_bucket_id}`, Number(r[metricKey]) || 0);
    });
    dims.sort((a, b) => (a.ord - b.ord));
    return { dims, valueFor: (k, m) => val.get(`${m}|${k}`) || 0 };
  }, [compare, metricKey]);

  const hasData = (compare?.summary || []).some(
    (r) => Number(r.total_amount) > 0 || Number(r.outreach) > 0
  );

  const win = compare?.window;
  const periodWord = granularity === "Day" ? "day" : granularity === "Weekly" ? "week" : "month";


  return (
    <Box sx={{ p: 2 }}>

      {/* ── Slicer header ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <MultiSelectDropdown
          label="Models"
          options={modelOptions}
          selected={selectedModels}
          onChange={setSelectedModels}
          placeholder="Select models…"
          minWidth={240}
        />

        <PillGroup
          label="View"
          options={FILTER_OPTIONS}
          value={filterType}
          onChange={setFilterType}
        />

        <PillGroup
          label="Granularity"
          options={GRAN_OPTIONS}
          value={granularity}
          onChange={setGranularity}
        />

        <PillGroup
          label="Date range"
          options={RANGE_OPTIONS}
          value={rangePreset}
          onChange={setRangePreset}
          groups={[3, 4, 2, 4]}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loadingModels && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading models…
        </Box>
      )}

      {!loadingModels && models.length === 0 && (
        <EmptyState
          title="No models to compare"
          hint="Create at least 2 models on the Models tab to enable comparisons."
        />
      )}

      {!loadingModels && models.length > 0 && selectedModels.length < 1 && (
        <EmptyState
          title="Select a model"
          hint="Pick one or more models above to see their baselines compared."
        />
      )}

      {!loadingModels && selectedModels.length >= 1 && (
        <>
          {/* Context caption */}
          <Box sx={{ mb: 2, fontSize: 11, color: "text.disabled" }}>
            {win && (
              <>
                Window: <Box component="span" sx={{ color: "text.secondary" }}>
                  {win.startDate} → {win.endDate}
                </Box>
                {`  (${win.days} day${win.days === 1 ? "" : "s"})`}
                {"  ·  "}
              </>
            )}
            <Box component="span" sx={{ color: "text.secondary" }}>
              {selectedModels.length} model{selectedModels.length === 1 ? "" : "s"}
            </Box>
            {"  ·  "}metric: <Box component="span" sx={{ color: "text.secondary" }}>{metricLabel}</Box>
          </Box>

          {loadingCompare && (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
              <CircularProgress size={20} /> Crunching baselines…
            </Box>
          )}

          {!loadingCompare && compare && !hasData && (
            <EmptyState
              title="No baseline data for the selected models"
              hint="Set up Contacts/Balance and per-channel rate inputs on the Baseline tab, then come back to compare."
            />
          )}

          {!loadingCompare && compare && hasData && (
            <>
              {/* ── Channel impact within each model (stacked bars) ── */}
              <SectionTitle>Channel impact by model — {metricLabel}</SectionTitle>
              <Typography sx={{ fontSize: 11, color: "text.disabled", mb: 1 }}>
                Each bar is a model; segments are its channels. Same-colored segments are the
                same channel across models, so you can see which channel drives each model and
                how that channel compares model-to-model. The label on each segment is its
                share of that model's total.
              </Typography>
              <Box sx={{ width: "100%", height: Math.max(220, orderedModels.length * 52 + 80), mb: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={channelBarData} layout="vertical"
                            margin={{ top: 8, right: 72, bottom: 4, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" horizontal={false} />
                    <XAxis type="number" tickFormatter={fmtCurrency} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="model" width={140} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value, name) => [fmtCurrency(value), name]}
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {channelNames.map((cn, i) => {
                      const fill = CHANNEL_COLORS[i % CHANNEL_COLORS.length];
                      return (
                        <Bar key={cn} dataKey={cn} stackId="a" fill={fill}
                             radius={i === channelNames.length - 1 ? [0, 3, 3, 0] : 0}>
                          <LabelList
                            dataKey={`${cn}__pct`}
                            content={(p) => <ChannelPctLabel {...p} fill={fill} />}
                          />
                          <LabelList
                            dataKey={`${cn}__endtotal`}
                            content={(p) => <ChannelTotalLabel {...p} />}
                          />
                        </Bar>
                      );
                    })}
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              {/* ── Summary table ── */}
              <SectionTitle>Window summary</SectionTitle>
              <Box sx={{
                mb: 4, border: "0.5px solid", borderColor: "divider",
                borderRadius: 2, overflow: "hidden",
              }}>
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: "1.4fr repeat(6, minmax(0,1fr))",
                  gap: 1, px: 1.5, py: 1, background: "#f7f7f7",
                  borderBottom: "0.5px solid", borderColor: "divider",
                  fontSize: 11, fontWeight: 500, color: "text.secondary",
                  textTransform: "uppercase", letterSpacing: "0.04em",
                }}>
                  <Box>Model</Box>
                  <Box sx={{ textAlign: "right" }}>Outreach</Box>
                  <Box sx={{ textAlign: "right" }}>Outreach $</Box>
                  <Box sx={{ textAlign: "right" }}>Payments $</Box>
                  <Box sx={{ textAlign: "right" }}>PTP $</Box>
                  <Box sx={{ textAlign: "right" }}>Total Coll. $</Box>
                  <Box sx={{ textAlign: "right" }}>Coll. %</Box>
                </Box>
                {(compare.summary || []).map((r, i) => {
                  // Coll% is the collection RATE (collected ÷ book worked each day),
                  // pre-computed by the endpoint. It is NOT total_amount ÷ outreach_balance
                  // — Outreach $ is total unique delinquency, a different (larger) base.
                  const collPct = r.collection_pct != null
                    ? Number(r.collection_pct)
                    : (Number(r.outreach_balance) > 0
                        ? Number(r.total_amount) / Number(r.outreach_balance)
                        : 0);
                  return (
                    <Box key={r.model_id} sx={{
                      display: "grid",
                      gridTemplateColumns: "1.4fr repeat(6, minmax(0,1fr))",
                      gap: 1, px: 1.5, py: "8px",
                      borderBottom: "0.5px solid", borderColor: "divider",
                      background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                      "&:last-of-type": { borderBottom: "none" },
                      alignItems: "center",
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 12, fontWeight: 600 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: colorById.get(r.model_id) }} />
                        {r.model_name}
                      </Box>
                      <Box sx={{ textAlign: "right", fontSize: 12 }}>{fmtCount(r.outreach)}</Box>
                      <Box sx={{ textAlign: "right", fontSize: 12 }}>{fmtCurrency(r.outreach_balance)}</Box>
                      <Box sx={{ textAlign: "right", fontSize: 12, color: PURPLE }}>{fmtCurrency(r.payments_amount)}</Box>
                      <Box sx={{ textAlign: "right", fontSize: 12, color: GREEN }}>{fmtCurrency(r.ptp_amount)}</Box>
                      <Box sx={{ textAlign: "right", fontSize: 13, fontWeight: 700, color: colorById.get(r.model_id) }}>
                        {fmtCurrency(r.total_amount)}
                      </Box>
                      <Box sx={{ textAlign: "right", fontSize: 12, fontWeight: 600 }}>{fmtPct(collPct)}</Box>
                    </Box>
                  );
                })}
              </Box>

              {/* ── Range (left) + per-period bars & delta (right) ── */}
              <SectionTitle>{`${metricLabel} — range & per-${periodWord} comparison`}</SectionTitle>
              {twoModels && (
                <Typography sx={{ fontSize: 12, mb: 1 }}>
                  <Box component="span" sx={{ color: "text.secondary" }}>Window difference</Box>{"  "}
                  <Box component="span" sx={{ fontWeight: 700, color: deltaTotal >= 0 ? GREEN : "#A32D2D" }}>
                    {deltaTotal >= 0 ? "+" : ""}{fmtCurrency(deltaTotal)}
                  </Box>{"  "}
                  <Box component="span" sx={{ color: "text.disabled" }}>
                    ({modelBars[0].name} − {modelBars[1].name})
                  </Box>
                </Typography>
              )}

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 4 }}>
                {/* LEFT — box-and-whisker range per model */}
                <Box sx={{ flex: "1 1 360px", minWidth: 300 }}>
                  <Typography sx={{ fontSize: 11, color: "text.disabled", mb: 1 }}>
                    {`Low / high range per ${periodWord} — whisker caps are the worst & best ${periodWord} `}
                    {`(labeled), box is the middle 50%, line is the median.`}
                  </Typography>
                  <ModelBoxPlot stats={boxStats} height={360} periodWord={periodWord} />
                </Box>

                {/* RIGHT — per-period grouped bars (models side by side) + delta */}
                <Box sx={{ flex: "1 1 360px", minWidth: 300 }}>
                  <Typography sx={{ fontSize: 11, color: "text.disabled", mb: 1 }}>
                    {`Each ${periodWord}, models side by side`}
                    {twoModels ? "; the line is their difference (right axis, 0 = tied)." : "."}
                  </Typography>
                  <Box sx={{ width: "100%", height: 360 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={periodSeries} barCategoryGap="22%" barGap={2}
                                     margin={{ top: 10, right: twoModels ? 52 : 16, bottom: 4, left: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }}
                               interval="preserveStartEnd" minTickGap={16} />
                        <YAxis yAxisId="main" tick={{ fontSize: 11 }} tickFormatter={fmtCurrency} width={58} />
                        {twoModels && (
                          <YAxis yAxisId="delta" orientation="right" tick={{ fontSize: 11 }}
                                 tickFormatter={fmtCurrency} width={56} />
                        )}
                        <Tooltip
                          cursor={{ fill: "rgba(0,0,0,0.04)" }}
                          content={(props) => <PeriodTooltip {...props} modelBars={modelBars} />}
                        />
                        {modelBars.map((m) => (
                          <Bar key={m.id} yAxisId="main" dataKey={`m${m.id}`} name={m.name}
                               fill={m.color} radius={[3, 3, 0, 0]} maxBarSize={44} />
                        ))}
                        {twoModels && <ReferenceLine yAxisId="delta" y={0} stroke="#ccc" />}
                        {twoModels && (
                          <Line yAxisId="delta" type="monotone" dataKey="delta"
                                name={`Δ ${modelBars[0].name} − ${modelBars[1].name}`}
                                stroke="#444" strokeWidth={1.75} strokeDasharray="4 3" dot={false} />
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </Box>

              {/* ── By-DPD matrix ── */}
              {dpdMatrix.dims.length > 0 && (
                <Box sx={{ mt: 3, mb: 4 }}>
                  <SectionTitle>By DPD bucket — {metricLabel}</SectionTitle>
                  <Box sx={{ overflowX: "auto" }}>
                    <Box sx={{
                      minWidth: 160 + orderedModels.length * 120,
                      border: "0.5px solid", borderColor: "divider",
                      borderRadius: 2, overflow: "hidden",
                    }}>
                      <Box sx={{
                        display: "grid",
                        gridTemplateColumns: `160px repeat(${orderedModels.length}, minmax(110px, 1fr))`,
                        gap: 1, px: 1.5, py: 1, background: "#f7f7f7",
                        borderBottom: "0.5px solid", borderColor: "divider",
                        fontSize: 11, fontWeight: 500,
                      }}>
                        <Box sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.04em" }}>DPD Bucket</Box>
                        {orderedModels.map((m) => (
                          <Box key={m.model_id} sx={{ textAlign: "right", color: m.color, fontWeight: 600 }}>{m.model_name}</Box>
                        ))}
                      </Box>
                      {dpdMatrix.dims.map((dim, i) => (
                        <Box key={dim.key} sx={{
                          display: "grid",
                          gridTemplateColumns: `160px repeat(${orderedModels.length}, minmax(110px, 1fr))`,
                          gap: 1, px: 1.5, py: "7px",
                          borderBottom: "0.5px solid", borderColor: "divider",
                          background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                          "&:last-of-type": { borderBottom: "none" },
                          alignItems: "center",
                        }}>
                          <Box sx={{ fontSize: 12, fontWeight: 500 }}>{dim.label}</Box>
                          {orderedModels.map((m) => (
                            <Box key={m.model_id} sx={{ textAlign: "right", fontSize: 12 }}>
                              {fmtCurrency(dpdMatrix.valueFor(dim.key, m.model_id))}
                            </Box>
                          ))}
                        </Box>
                      ))}
                      <Box sx={{
                        display: "grid",
                        gridTemplateColumns: `160px repeat(${orderedModels.length}, minmax(110px, 1fr))`,
                        gap: 1, px: 1.5, py: "9px", background: "#f0f5ff",
                        borderTop: `1px solid ${BLUE}33`, alignItems: "center",
                      }}>
                        <Box sx={{ fontSize: 11, fontWeight: 700, color: BLUE }}>Total</Box>
                        {orderedModels.map((m) => {
                          const sum = dpdMatrix.dims.reduce((s, dim) => s + dpdMatrix.valueFor(dim.key, m.model_id), 0);
                          return (
                            <Box key={m.model_id} sx={{ textAlign: "right", fontSize: 12, fontWeight: 700, color: BLUE }}>
                              {fmtCurrency(sum)}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}

              <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
                Showing the <b>{filterType}</b> path at <b>{granularity}</b> granularity. Calendar
                effect is applied to the projection; persona effects are not (they come with
                the forecast engine).
              </Typography>
            </>
          )}
        </>
      )}
    </Box>
  );
}